import type {
  AddonRasterAssetUploadInput,
  AddonRasterGenerationInput,
  AddonRasterLabJobInput,
} from '../editor/addons/types';

export function cloudRequestHeaders(
  accessToken: string,
  options: RequestInit,
  workspaceId?: string,
): Headers {
  const headers = new Headers(options.headers);
  headers.set('authorization', `Bearer ${accessToken}`);
  if (workspaceId) headers.set('x-workspace-id', workspaceId);
  if (options.body && !headers.has('content-type')) headers.set('content-type', 'application/json');
  return headers;
}

export function generationRequestInit(input: AddonRasterGenerationInput): RequestInit {
  return {
    method: 'POST',
    headers: { 'idempotency-key': input.idempotencyKey },
    body: JSON.stringify({
      prompt: input.prompt,
      model: input.model,
      ...(input.size ? { size: input.size } : {}),
    }),
    ...(input.signal ? { signal: input.signal } : {}),
  };
}

/**
 * Raster Lab wire requests.
 *
 * These paths and bodies follow docs/editor/ADR-003. Workspace identity stays
 * in X-Workspace-Id, idempotency stays in Idempotency-Key, and AbortSignal is
 * request metadata; none of the three is serialized into provider-facing JSON.
 */
export function rasterAssetIntentRequestInit(
  input: AddonRasterAssetUploadInput,
  sha256: string,
): RequestInit {
  return {
    method: 'POST',
    headers: { 'idempotency-key': input.idempotencyKey },
    body: JSON.stringify({
      kind: input.kind,
      contentType: input.blob.type,
      sizeBytes: input.blob.size,
      width: input.width,
      height: input.height,
      sha256,
    }),
    ...(input.signal ? { signal: input.signal } : {}),
  };
}

export function rasterAssetContentRequestInit(
  blob: Blob,
  signal?: AbortSignal,
): RequestInit {
  return {
    method: 'PUT',
    headers: { 'content-type': blob.type },
    body: blob,
    ...(signal ? { signal } : {}),
  };
}

export function rasterAssetCommitRequestInit(
  sha256: string,
  signal?: AbortSignal,
): RequestInit {
  return {
    method: 'POST',
    body: JSON.stringify({ sha256 }),
    ...(signal ? { signal } : {}),
  };
}

export function rasterJobRequestInit(input: AddonRasterLabJobInput): RequestInit {
  return {
    method: 'POST',
    headers: { 'idempotency-key': input.idempotencyKey },
    body: JSON.stringify({
      routeId: input.routeId,
      capabilitiesRevision: input.capabilitiesRevision,
      sourceAssetId: input.sourceAssetId,
      ...(input.maskAssetId ? { maskAssetId: input.maskAssetId } : {}),
      ...(input.prompt ? { prompt: input.prompt } : {}),
      ...(input.options ? { options: input.options } : {}),
    }),
    ...(input.signal ? { signal: input.signal } : {}),
  };
}
